import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, CloseOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Form, Input, Row, Spin, Switch } from 'antd';
import axios from 'axios';
import { axiosErrorHandler } from '../../../../../swzutils';
import HelpLabel from "../../../components/HelpLabel";
import { validateEmailCsv } from "../../../../../swzutils/ValidationUtil";
const AdvisoryFormItem = ({ advisory, onEmailEnabledSwitched, onNotificationEnabledSwitched }) => {
    const enableEmailFieldName = advisory.code + "_emailEnabled";
    const emailsInputFieldName = advisory.code + "_emailAddresses";
    const enableNotificationFieldName = advisory.code + "_emailNotification";
    return (_jsxs("div", { className: "advisory-field", children: [_jsx("h3", { children: advisory.name }), _jsx("p", { children: advisory.helpText }), _jsxs(Row, { align: 'middle', children: [_jsx(Col, { children: _jsx(Form.Item, { name: enableEmailFieldName, valuePropName: 'checked', className: "switch", initialValue: advisory.emailEnabled, children: _jsx(Switch, { checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), defaultChecked: advisory.emailEnabled, onChange: (checked) => onEmailEnabledSwitched(advisory, checked) }) }) }), _jsx(Col, { children: _jsx("p", { style: { fontSize: '11pt', paddingLeft: '5px' }, children: "Send an email" }) })] }), _jsx(Form.Item, { name: emailsInputFieldName, className: "form-indent", initialValue: advisory.emailAddresses, rules: [
                    { required: advisory.emailEnabled, message: 'Please input one or more emails addresses separated by a comma.' },
                    { max: 1000, message: 'Please limit the email address to 1000 characters.' },
                    { validator: validateEmailCsv }
                ], children: _jsx(Input, { prefix: _jsx(MailOutlined, { style: { color: 'rgba(0,0,0,.25)' } }), placeholder: "Email Addresses", disabled: !advisory.emailEnabled }) }), _jsxs(Row, { align: 'middle', children: [_jsx(Col, { children: _jsx(Form.Item, { name: enableNotificationFieldName, valuePropName: 'checked', initialValue: advisory.notificationEnabled, className: "switch", children: _jsx(Switch, { checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), defaultChecked: advisory.notificationEnabled, onChange: (checked) => onNotificationEnabledSwitched(advisory, checked) }) }) }), _jsx(Col, { children: _jsx("p", { style: { fontSize: '11pt', paddingLeft: '5px' }, children: "Raise a notification" }) })] }), _jsx(Divider, {})] }));
};
const SystemAdvisoriesForm = ({ entityName, entityId, showCancelButton, onSaveCompleted, onCancel }) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [advisories, setAdvisories] = useState([]);
    const [form] = Form.useForm();
    useEffect(() => {
        if (entityName && entityId) {
            loadSystemAdvisories();
        }
    }, [entityName, entityId]);
    const loadSystemAdvisories = () => {
        setShowSpinner(true);
        axios.get(`/admin/v1/${entityName}/${entityId}/advisories`)
            .then(response => setAdvisories(response.data))
            .catch(axiosErrorHandler)
            .finally(() => setShowSpinner(false));
    };
    const save = (formValues) => {
        setShowSpinner(true);
        const updateRequest = advisories.map(advisory => {
            const emailsInputFieldName = advisory.code + "_emailAddresses";
            return {
                code: advisory.code,
                emailEnabled: advisory.emailEnabled,
                emailAddresses: formValues[emailsInputFieldName],
                smsEnabled: false,
                smsMobileNumbers: null,
                notificationEnabled: advisory.notificationEnabled
            };
        });
        axios.put(`/admin/v1/${entityName}/${entityId}/advisories`, updateRequest)
            .then(( /*response*/) => onSaveCompleted())
            .catch(axiosErrorHandler)
            .finally(() => setShowSpinner(false));
    };
    const onEmailEnabledSwitchedChange = (changedAdvisory, emailEnabled) => {
        const updatedAdvisories = advisories.map(advisory => {
            if (changedAdvisory.code === advisory.code) {
                return {
                    ...advisory,
                    emailEnabled
                };
            }
            return advisory;
        });
        setAdvisories(updatedAdvisories);
    };
    const onNotificationEnabledSwitched = (changedAdvisory, notificationEnabled) => {
        const updatedAdvisories = advisories.map(advisory => {
            if (changedAdvisory.code === advisory.code) {
                return {
                    ...advisory,
                    notificationEnabled
                };
            }
            return advisory;
        });
        setAdvisories(updatedAdvisories);
    };
    const advisoryFormItems = useMemo(() => {
        return advisories.map(advisory => {
            return (_jsx(AdvisoryFormItem, { advisory: advisory, onEmailEnabledSwitched: onEmailEnabledSwitchedChange, onNotificationEnabledSwitched: onNotificationEnabledSwitched }, advisory.code));
        });
    }, [advisories]);
    return (_jsx(Spin, { spinning: showSpinner, children: _jsxs(Form, { layout: "vertical", form: form, onFinish: save, children: [_jsx(Row, { gutter: 10, className: "no-margin", children: _jsx(Col, { xs: 24, children: _jsxs(Card, { title: (_jsx(HelpLabel, { title: "System Advisories", helpText: "Set one or more email addresses to receive the relevant notification and set the raise notification to receive a live alert on the top left hand side of your screen. Note: for multiple email addresses place a comma after each email before adding another email address." })), type: "inner", variant: "outlined", children: [_jsx("p", { children: "NOTE: Multiple email addresses can be added separated with a comma" }), advisoryFormItems] }) }) }), showCancelButton ?
                    _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { htmlType: "button", onClick: form.submit, block: true, type: "default", size: "large", children: "Save" }) }), _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { htmlType: "button", onClick: onCancel, block: true, type: "default", size: "large", children: "Cancel" }) })] })
                    :
                        _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { htmlType: "button", onClick: form.submit, block: true, type: "default", size: "large", children: "Update" }) })] })] }) }));
};
export default SystemAdvisoriesForm;
